// @flow
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import momentPlugin from '@fullcalendar/moment';
import ptbrLang from '@fullcalendar/core/locales/pt-br';
import esLang from '@fullcalendar/core/locales/es';
import classNames from 'classnames';
import moment from 'moment';
import { get, find, map, forEach, filter, isEqual, findKey } from 'lodash';
import { Calendar as FC } from '@fullcalendar/core';
import type { WorkHours } from '@types';
import { Log, isPresent } from '@helpers/helpers';
import CalendarEvent, { getEventClassNames } from '../Calendar/CalendarEvent';
import '../Calendar/Calendar.scss';

type Props = {
  activeModal: ?string,
  appointments: Object[],
  selectedDate: Object,
  clinic: Object,
  clinicDoctors: Object,
  filter: Object,
  loadingItems: Object,
  isFinatialEnabled: boolean,
  user: Object,
  isLoading: boolean,
  workHours: WorkHours,
  language: string,
  onShowAppointment: Function,
  onNewAppointmentClick: Function,
  _: Function,
};

const plugins = [interactionPlugin, momentPlugin, timeGridPlugin, dayGridPlugin];

const getNow = (clinic: any) => {
  const forceTimezone = get(clinic, ['forceTimezone'], null);
  return isPresent(forceTimezone) ? moment.utc().add(forceTimezone, 'hours').format() : moment().format();
};

const DayView = ({
  activeModal, appointments, selectedDate, clinic, clinicDoctors,
  filter: agendaFilter, loadingItems, isFinatialEnabled, user,
  isLoading, workHours, language,
  onShowAppointment, onNewAppointmentClick, _,
}: Props) => {
  const api = useRef(null);
  const [ghostAppointment, setGhostAppointment] = useState(null);
  // The day last scrolled to its working hours
  const scrolledDay = useRef(null);
  const lastAppointmentFetch = useRef(null);

  const selectedDateStr = useMemo(() => selectedDate.format('YYYY-MM-DD'), [selectedDate]);

  const dayAppointments = useMemo(() => filter(appointments, (apt) => (
    moment.utc(apt.start).format('YYYY-MM-DD') === selectedDateStr
  )), [appointments, selectedDateStr]);

  const clinicNow = useMemo(() => getNow(clinic), [clinic]);
  const scrollTime = useMemo(() => get(clinic, 'businessHoursStart') || '9:00', [clinic]);
  const snapDuration = useMemo(() => get(clinic, 'snapDuration') || '00:15:00', [clinic]);
  const locale = useMemo(() => (language === 'es' ? esLang : ptbrLang), [language]);

  const defaultDuration = get(clinicDoctors[(
    (get(agendaFilter, ['doctor', 'length']) ? get(agendaFilter, ['doctor', String(0)]) : null) ||
    (!get(user, ['roleType']) === 'Doctor' && !get(user, ['advancedData', 'doctorId'])) ||
    get(agendaFilter, ['doctor', String(1)]) ||
    findKey(clinicDoctors)
  )], 'defaultAppointmentDuration');

  const viewsConfig = useMemo(() => ({
    day: {
      dayHeaderFormat: 'dddd',
      titleFormat: 'ddd, DD/MM',
    },
  }), []);

  const businessHours = useMemo(() => ({
    startTime: get(clinic, 'businessHoursStart') || '9:00',
    endTime: get(clinic, 'businessHoursEnd') || '18:00',
    daysOfWeek: [1, 2, 3, 4, 5],
  }), [clinic]);

  const nowIndicator = useMemo(() => get(clinic, ['nowIndicator'], true), [clinic]);

  const getAppointment = useCallback((id: string = '') => ({
    id,
    ...(find(dayAppointments, ap => parseInt(get(ap, 'id', ''), 10) === parseInt(id, 10)) || {}),
  }), [dayAppointments]);

  const eventAllow = useCallback(
    (dropLocation: Object, event: Object) => !get(loadingItems, String(get(event, 'id', '')), false),
    [loadingItems],
  );

  const eventOrder = useCallback((a, b) => {
    const eventA = find(dayAppointments, ev => String(get(ev, 'id', '')) === String(get(a, 'id', '')));
    const eventB = find(dayAppointments, ev => String(get(ev, 'id', '')) === String(get(b, 'id', '')));
    return parseInt(get(eventA, 'doctorId', 0), 10) - parseInt(get(eventB, 'doctorId', 0), 10);
  }, [dayAppointments]);

  const appointmentSource = useCallback((dt, callback: Function) => {
    const appointmentsList = map(dayAppointments, appointment => {
      const canChange = !get(loadingItems, get(appointment, 'id'), false) && String(appointment.doctorId) !== '1';
      return {
        ...appointment,
        startEditable: canChange,
        durationEditable: canChange,
        doctorId: appointment.doctorId,
      };
    });
    const renderAppointments = ghostAppointment ? [...appointmentsList, ghostAppointment] : appointmentsList;
    callback(renderAppointments);
  }, [dayAppointments, ghostAppointment, loadingItems]);

  const workHoursSource = useCallback((dt, callback: Function) => {
    const doctorFilterId = get(agendaFilter, ['doctor', '0']);
    if (doctorFilterId > 0) {
      const doctorWorkHours: Object = workHours || {};
      const dayOfWeek = moment.utc(selectedDateStr).day();
      const doctorWorkHourEvents = [];

      const dayWorkHourEvents = filter(doctorWorkHours, item => get(item, 'weekday') === dayOfWeek);

      forEach(dayWorkHourEvents, dayWorkHourEvent => {
        if (!dayWorkHourEvent) return;

        const start = moment.utc(`${selectedDateStr} ${dayWorkHourEvent.start}:00`);
        const end = moment.utc(`${selectedDateStr} ${dayWorkHourEvent.end}:00`);

        doctorWorkHourEvents.push({
          id: `${doctorFilterId}-${moment.utc(selectedDateStr).unix()}-`,
          doctorId: doctorFilterId,
          start: start.toISOString(),
          end: end.toISOString(),
          display: 'background',
          classNames: ['bg-doctor-workhour'],
          backgroundColor: 'rgb(143, 223, 130)',
          isBackgroundEvent: true,
        });
      });
      callback(filter(doctorWorkHourEvents, item => item !== null));
    } else {
      callback([]);
    }
  }, [agendaFilter, selectedDateStr, workHours]);

  const getMergedEventData = useCallback((eventApi) => ({
    ...eventApi,
    ...eventApi.extendedProps,
    ...getAppointment(get(eventApi, 'id', '')),
  }), [getAppointment]);

  const renderEventContent = useCallback((eventInfo) => {
    if (eventInfo.event.display === 'background') {
      return null;
    }

    const eventData = getMergedEventData(eventInfo.event);

    return (
      <CalendarEvent
        event={eventData}
        isFinatialEnabled={isFinatialEnabled}
        loadingItems={loadingItems}
        clinicDoctors={clinicDoctors}
        agendaFilter={agendaFilter}
        isMobile
        classList=""
        clinic={clinic}
        user={user}
        snapDuration={snapDuration}
        _={_}
      />
    );
  }, [
    getMergedEventData, isFinatialEnabled, loadingItems, clinicDoctors,
    agendaFilter, clinic, user, snapDuration, _,
  ]);

  const renderEventClassNames = useCallback((eventInfo) => {
    const eventData = getMergedEventData(eventInfo.event);
    return getEventClassNames({
      event: eventData,
      clinicDoctors,
      agendaFilter,
      user,
      snapDuration,
    });
  }, [getMergedEventData, clinicDoctors, agendaFilter, user, snapDuration]);

  const handleDateClick = useCallback((params: Object) => {
    const start = moment.utc(params.date);
    let startT = moment(start);
    let endT = moment(start);

    if (moment(start).minutes() >= 30) {
      endT = moment(start).set('minutes', 30).add(defaultDuration, 'minutes');
    } else {
      endT = moment(start).set('minutes', 0).add(defaultDuration, 'minutes');
    }

    if (startT.minutes() >= 30) {
      startT = startT.clone().set('minutes', 30);
    } else {
      startT = startT.clone().set('minutes', 0);
    }

    setGhostAppointment({
      start: startT.toISOString(),
      end: endT.toISOString(),
      allDay: false,
      title: '',
      extendedProps: {
        full: false,
        time: moment.utc(start).toISOString(),
        date: moment.utc(start).format('YYYY-MM-DD'),
        badge: null,
        patient: null,
        restricted: false,
        isGhost: true,
        duration: defaultDuration,
        room: 1,
        doctorId: 72,
        notes: '',
      },
    });
  }, [defaultDuration]);

  const handleSelect = useCallback(() => {}, []);

  const handleEventDrop = useCallback(() => {}, []);

  const handleEventResize = useCallback(() => {}, []);

  const handleEventClick = useCallback((dt) => {
    const event = { ...dt.event.extendedProps, ...getAppointment(get(dt, ['event', 'id'], '')) };

    if (event.restricted) return false;

    if (event.isGhost) {
      const minutes = moment.utc(event.time).minutes();
      const eventTime = moment.utc(event.time).set('minutes', minutes >= 30 ? 30 : 0);

      onNewAppointmentClick(
        eventTime.format('YYYY-MM-DD HH:mm:ss'),
        eventTime.add(defaultDuration, 'minutes').format('YYYY-MM-DD HH:mm:ss'),
      );
      setGhostAppointment(null);
    } else {
      onShowAppointment(event);
    }
    return undefined;
  }, [getAppointment, onNewAppointmentClick, defaultDuration, onShowAppointment]);

  useEffect(() => {
    try {
      if (activeModal !== 'NewAppointmentModal' && api.current) {
        api.current.unselect();
      }
      if (activeModal !== 'NewAppointmentModal') {
        setGhostAppointment(null);
      }
    } catch (e) {
      Log.error(e);
    }
  }, [activeModal]);

  // Each day opens on its working hours, once it's on screen: right away for a day already loaded
  // (a refresh in the background doesn't count as loading), after its request for one that isn't
  useEffect(() => {
    try {
      if (scrolledDay.current !== selectedDateStr && api.current && !isLoading) {
        scrolledDay.current = selectedDateStr;
        const _api: FC = api.current;
        setTimeout(() => {
          try {
            _api.scrollToTime(`${scrollTime}`);
          } catch (e) {
            //
          }
        }, 200);
      }
    } catch (e) {
      //
    }
  }, [scrollTime, isLoading, selectedDateStr]);

  useEffect(() => {
    setGhostAppointment(null);
  }, [selectedDateStr]);

  useEffect(() => {
    try {
      if (api.current) {
        api.current.refetchEvents();
      }
    } catch (e) {
      Log.error(e);
    }
  }, [ghostAppointment]);

  useEffect(() => {
    try {
      if (api.current && !isLoading) {
        api.current.gotoDate(moment.utc(selectedDateStr).toDate());
      }
    } catch (e) {
      Log.error(e);
    }
  }, [selectedDateStr, isLoading]);

  useEffect(() => {
    try {
      if (api.current && !isLoading && !isEqual(lastAppointmentFetch.current, dayAppointments)) {
        api.current.removeAllEvents();
        api.current.refetchEvents();
        lastAppointmentFetch.current = dayAppointments;
      }
    } catch (e) {
      Log.error(e);
    }
  }, [isLoading, dayAppointments, loadingItems]);

  return (
    <div className="android-day-view">
      <div id="agenda-calendar">
        <section className="agenda">
          <div className={classNames('calendar-holder', { isMobile: true })}>
            <FullCalendar
              allDaySlot={false}
              firstDay={1}
              slotDuration="00:30:00"
              scrollTime={scrollTime}
              scrollTimeReset={false}
              snapDuration={snapDuration}
              slotLabelFormat="HH:mm"
              eventTimeFormat="HH:mm"
              header={false}
              nowIndicator={nowIndicator}
              unselectAuto={false}
              eventOverlap
              selectable={false}
              editable
              eventStartEditable={false}
              eventDurationEditable={false}
              slotEventOverlap={false}
              eventClassNames={renderEventClassNames}
              initialDate={moment.utc(selectedDateStr).toDate()}
              displayEventEnd={false}
              displayEventTime={false}
              hiddenDays={[]}
              timeZoneParam="UTC"
              timeZone={false}
              locale={locale}
              height="100%"
              eventAllow={eventAllow}
              eventOrder={eventOrder}
              initialView="timeGridDay"
              now={clinicNow}
              views={viewsConfig}
              eventSources={[appointmentSource, workHoursSource]}
              businessHours={businessHours}
              plugins={plugins}
              eventContent={renderEventContent}
              select={handleSelect}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              selectMirror
              ref={ref => { api.current = ref && ref.getApi(); }}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

// $FlowFixMe
export default React.memo(DayView);
